import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export interface MediaUser {
  id: number;
  first_name: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
  role?: string;
  roles?: string[];
  last_active_role?: string;
  get_photo_url?: string | null;
  photo_url?: string | null;
  profile_picture?: string | null;
  is_staff?: boolean;
  is_superuser?: boolean;
}

export interface MediaAsset {
  id: number;
  title: string;
  alt_text?: string;
  caption?: string;
  media_type: "image" | "video" | "audio" | "document" | "other";
  file?: string | null;
  file_size?: number | null;
  file_size_formatted?: string | null;
  external_url?: string | null;
  url: string;
  thumbnail_url?: string;
  uploaded_by?: MediaUser | null;
  uploaded_at: string;
  updated_at: string;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not yet initialized
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

const BASE_ENDPOINT = "/media-library/assets/";

export const mediaService = {
  // GET: Fetch list of media assets
  async getMediaAssets(params?: { media_type?: string; search?: string }, signal?: AbortSignal): Promise<MediaAsset[]> {
    const client = getClient();
    const queryParams: Record<string, string> = {};

    if (params?.media_type && params.media_type !== "all") {
      queryParams.media_type = params.media_type;
    }
    if (params?.search && params.search.trim() !== "") {
      queryParams.search = params.search.trim();
    }

    const response = await client.get(BASE_ENDPOINT, { params: queryParams, signal });

    if (response && (response.status === 200 || response.status === 201)) {
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.results)) {
        return data.results;
      }
    }
    return [];
  },

  // POST: Create a new media asset
  async createMediaAsset(data: FormData | Partial<MediaAsset>): Promise<MediaAsset> {
    const client = getClient();
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "application/json" } };

    const response = await client.post(BASE_ENDPOINT, data, config);

    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to create media asset (HTTP ${response?.status || "Unknown"})`);
  },

  // PATCH: Update existing media asset
  async updateMediaAsset(id: number, data: FormData | Partial<MediaAsset>): Promise<MediaAsset> {
    const client = getClient();
    const endpoint = `${BASE_ENDPOINT}${id}/`;
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "application/json" } };

    const response = await client.patch(endpoint, data, config);

    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to update media asset #${id} (HTTP ${response?.status || "Unknown"})`);
  },

  // DELETE: Remove media asset
  async deleteMediaAsset(id: number): Promise<void> {
    const client = getClient();
    const endpoint = `${BASE_ENDPOINT}${id}/`;

    const response = await client.delete(endpoint);

    if (response && (response.status === 200 || response.status === 204)) {
      return;
    }
    throw new Error(`Failed to delete media asset #${id} (HTTP ${response?.status || "Unknown"})`);
  },
};
